import { MapPin, Download, Smartphone, Monitor, QrCode, Check } from 'lucide-react';
import { useState } from 'react';
import { useInstallApp, InstallHelpModal } from './InstallAppButton';
import ForceRefreshButton from './ForceRefreshButton';
import AtriumLogo from './AtriumLogo';

export default function Footer({ settings }) {
  const name = settings?.footer_name || 'Dreams Rose Hall\u00ae Resort & Spa';
  const tagline = settings?.footer_tagline || 'Montego Bay, Jamaica \u2014 Hurricane Reconstruction Program';
  const [showQR, setShowQR] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const { triggerInstall, installed, isIos, canPrompt } = useInstallApp();

  const handleInstall = async (e) => {
    e.preventDefault();
    if (installed) return;
    if (isIos || !canPrompt) { setShowHelp(true); return; }
    const { needsHelp } = await triggerInstall();
    if (needsHelp) setShowHelp(true);
  };

  return (
    <footer data-testid="footer" className="py-12 px-4 md:px-8" style={{ backgroundColor: '#1A2E2E' }}>
      <div className="max-w-7xl mx-auto">
        {/* Install App Section */}
        <div className="flex flex-col items-center gap-6 mb-10 pb-10" style={{ borderBottom: '1px solid rgba(163,212,212,0.15)' }}>
          <div className="flex items-center gap-2">
            <Download className="w-4 h-4" style={{ color: '#A3D4D4' }} />
            <span className="text-sm font-bold tracking-wide" style={{ fontFamily: 'Manrope, sans-serif', color: '#A3D4D4' }}>
              Get the App
            </span>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {installed && (
              <span
                data-testid="footer-install-installed"
                className="flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold"
                style={{ backgroundColor: '#22C55E20', color: '#22C55E', fontFamily: 'Manrope' }}
              >
                <Check className="w-4 h-4" /> App Installed on This Device
              </span>
            )}

            {!installed && (
              <>
                <button
                  type="button"
                  onClick={handleInstall}
                  data-testid="install-mobile"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold transition-all hover:-translate-y-0.5"
                  style={{ backgroundColor: '#1F4FBE', color: 'white', fontFamily: 'Manrope' }}
                >
                  <Smartphone className="w-4 h-4" />
                  Install on Mobile
                </button>

                <button
                  type="button"
                  onClick={handleInstall}
                  data-testid="install-mac"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold transition-all hover:-translate-y-0.5"
                  style={{ backgroundColor: 'rgba(163,212,212,0.15)', color: '#A3D4D4', fontFamily: 'Manrope' }}
                >
                  <Monitor className="w-4 h-4" />
                  Install on Mac
                </button>

                <button
                  type="button"
                  onClick={handleInstall}
                  data-testid="install-pc"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold transition-all hover:-translate-y-0.5"
                  style={{ backgroundColor: 'rgba(163,212,212,0.15)', color: '#A3D4D4', fontFamily: 'Manrope' }}
                >
                  <Monitor className="w-4 h-4" />
                  Install on PC
                </button>
              </>
            )}

            {/* QR Code toggle */}
            <button
              onClick={() => setShowQR(!showQR)}
              data-testid="show-qr-btn"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold transition-all hover:-translate-y-0.5"
              style={{ backgroundColor: showQR ? '#FFB3A6' : 'rgba(163,212,212,0.15)', color: showQR ? '#1A2E2E' : '#A3D4D4', fontFamily: 'Manrope' }}
            >
              <QrCode className="w-4 h-4" />
              {showQR ? 'Hide QR Code' : 'Scan QR Code'}
            </button>
          </div>

          {/* QR Code */}
          {showQR && (
            <div className="flex flex-col items-center gap-3 animate-fade-slide-up">
              <div className="bg-white p-4 rounded-2xl shadow-lg">
                <img src="/qr-code.png" alt="QR Code to install app" className="w-40 h-40" data-testid="qr-code-img" />
              </div>
              <p className="text-[10px] text-center max-w-xs" style={{ fontFamily: 'Manrope', color: '#7A8C8C' }}>
                Scan this QR code with your phone camera to open the app. Then tap "Add to Home Screen" to install it.
              </p>
            </div>
          )}

          <p className="text-[10px] text-center max-w-md" style={{ fontFamily: 'Manrope', color: '#7A8C8C' }}>
            This is a Progressive Web App (PWA). On mobile, tap "Add to Home Screen" in your browser menu. On desktop (Chrome/Edge), click the install icon in the address bar.
          </p>

          {/* Update / Clear Cache block - prominent so users can find it easily */}
          <div
            className="w-full max-w-xl mt-4 p-5 rounded-2xl flex flex-col items-center gap-3 text-center"
            style={{ backgroundColor: 'rgba(163,212,212,0.08)', border: '1px solid rgba(163,212,212,0.25)' }}
          >
            <span className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: '#A3D4D4', fontFamily: 'Manrope' }}>
              Get the latest updates
            </span>
            <p className="text-sm max-w-md" style={{ fontFamily: 'Manrope', color: '#EAE5D9' }}>
              Not seeing the newest reconstruction photos, videos, or renders? Click below to clear your local cache and pull the freshest version of the app.
            </p>
            <ForceRefreshButton variant="pill" label="Clear Cache & Refresh App" className="text-sm px-5 py-2.5" />
            <p className="text-[11px] max-w-md" style={{ fontFamily: 'Manrope', color: '#7A8C8C' }}>
              Safe to use anytime — your admin login and saved settings are kept.
            </p>
          </div>
        </div>

        {/* Footer Info */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4" style={{ color: '#A3D4D4' }} />
            <span className="text-base font-medium" style={{ fontFamily: "'Cinzel', 'Cormorant Garamond', serif", color: '#EAE5D9' }}>
              {name}
            </span>
          </div>
          <p className="text-xs" style={{ fontFamily: 'Manrope, sans-serif', color: '#7A8C8C' }}>
            {tagline}
          </p>
          <p className="text-xs flex flex-wrap items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#7A8C8C' }}>
            <span>&copy; {new Date().getFullYear()} Rose Hall Resort. All rights reserved.</span>
            <span style={{ color: '#A3D4D4' }}>·</span>
            <span data-testid="footer-atrium-attrib" className="inline-flex items-center gap-1.5">
              Powered by
              <AtriumLogo variant="inline" size={18} accent="#A3D4D4" text="#EAE5D9" />
            </span>
          </p>
        </div>

        {/* Developer credit — small, understated, separated from the property
            footer line by a hairline. Includes the Heavenly AI Solutions logo
            and the lead developer. */}
        <div
          className="mt-8 pt-6 flex flex-col sm:flex-row items-center justify-center gap-3 text-center"
          style={{ borderTop: '1px solid rgba(163,212,212,0.1)' }}
          data-testid="footer-developer-credit"
        >
          {/* Logo is a transparent PNG so it sits directly on the dark navy
              footer without a white plate. Drop-shadow gives it a subtle
              lift so the gold/navy elements still read against the navy. */}
          <img
            src="/logo-heavenly-ai.png"
            alt="Heavenly AI Solutions"
            className="h-10 w-auto object-contain"
            style={{ filter: 'drop-shadow(0 4px 12px rgba(255,255,255,0.18))' }}
          />
          <p className="text-[11px]" style={{ fontFamily: 'Manrope, sans-serif', color: '#7A8C8C' }}>
            Platform developed by{' '}
            <a
              href="https://heavenlyaisolutions.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#A3D4D4', fontWeight: 700 }}
              className="hover:underline"
            >
              Heavenly AI Solutions
            </a>
            <span className="mx-1.5" style={{ color: '#A3D4D4' }}>·</span>
            led by <span style={{ color: '#EAE5D9', fontWeight: 700 }}>Andres Mocny</span>
          </p>
        </div>
      </div>
      {showHelp && <InstallHelpModal isIos={isIos} onClose={() => setShowHelp(false)} />}
    </footer>
  );
}
